/**
 * Extraction Utilities
 *
 * Helper functions for booking confirmation extraction via WebSocket.
 */

/**
 * Convert a File object to base64 string.
 *
 * @param {File} file - The file to convert
 * @returns {Promise<string>} Base64-encoded file data (without data URI prefix)
 *
 * @example
 * const base64 = await fileToBase64(pdfFile);
 * // Returns: "JVBERi0xLjQKJeLjz9MKNCAwIG9..." (base64 string only)
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      // reader.result is in format: "data:application/pdf;base64,JVBERi0x..."
      // We only want the base64 part after the comma
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };

    reader.onerror = (error) => {
      reject(new Error(`Failed to read file: ${error.message}`));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Generate a unique message ID for WebSocket messages.
 *
 * @returns {string} Unique message ID based on current timestamp
 *
 * @example
 * const msgId = generateMessageId();
 * // Returns: "1708012345678"
 */
export const generateMessageId = () => {
  return Date.now().toString();
};

/**
 * Format a date string from Date object to DD.MM.YYYY format.
 *
 * @param {Date|string} date - Date object or date string
 * @returns {string} Date in DD.MM.YYYY format
 *
 * @example
 * formatDate(new Date(2024, 5, 15))
 * // Returns: "15.06.2024"
 */
export const formatDateToDDMMYYYY = (date) => {
  if (!date) return '';

  const d = typeof date === 'string' ? new Date(date) : date;

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = d.getFullYear();

  return `${day}.${month}.${year}`;
};

/**
 * Parse a DD.MM.YYYY date string to Date object.
 *
 * @param {string} dateStr - Date string in DD.MM.YYYY format
 * @returns {Date|null} Date object or null if invalid
 *
 * @example
 * parseDateFromDDMMYYYY("15.06.2024")
 * // Returns: Date(2024, 5, 15)
 */
export const parseDateFromDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  const parts = dateStr.split('.');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  return new Date(year, month, day);
};

/**
 * Validate PNR format.
 *
 * @param {string} pnr - PNR/booking reference to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * validatePNR("ABC123") // Returns: true
 * validatePNR("XY") // Returns: false (too short)
 */
export const validatePNR = (pnr) => {
  if (!pnr) return false;

  // PNR is typically 5-7 characters (letters/numbers)
  const trimmed = pnr.trim();
  return trimmed.length >= 5 && trimmed.length <= 7;
};

/**
 * Validate IATA airport code format.
 *
 * @param {string} code - Airport code to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * validateAirportCode("MUC") // Returns: true
 * validateAirportCode("MU") // Returns: false (too short)
 */
export const validateAirportCode = (code) => {
  if (!code) return false;

  // IATA code is exactly 3 letters
  const trimmed = code.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(trimmed);
};

/**
 * Validate airline carrier code format.
 *
 * @param {string} code - Carrier code to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * validateCarrierCode("LH") // Returns: true
 * validateCarrierCode("L") // Returns: false (too short)
 */
export const validateCarrierCode = (code) => {
  if (!code) return false;

  // Carrier code is exactly 2 letters
  const trimmed = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed);
};

/**
 * Validate HH:MM time format.
 *
 * @param {string} time - Time string to validate
 * @returns {boolean} True if valid, false otherwise
 *
 * @example
 * validateTimeFormat("14:30") // Returns: true
 * validateTimeFormat("2:30") // Returns: false (missing leading zero)
 */
export const validateTimeFormat = (time) => {
  if (!time) return false;

  // HH:MM format (24-hour)
  const pattern = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
  return pattern.test(time);
};
